/**
 * src/components/MessagesModal.tsx
 *
 * File-level:
 * - Modal that lists conversation summaries and allows the user to open a chat or view a profile.
 *
 * Responsibilities:
 * - Provide a robust resolved displayName to consumers when opening chat (fallbacks included).
 * - Always navigate to /search-profile/:userId for the View action.
 * - Mark messages read and optimistically clear UI on Chat.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Users, MessageCircle, GitBranch } from 'lucide-react';
import { useNavigate } from 'react-router';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import useChat, { ChatMessage } from '@/hooks/useChat';
import useAuthStore from '@/stores/useAuthStore';
import { fetchJSON } from '@/utils/api';
import { trackModalView } from '@/utils/visitorTracking';
import { formatStoredDate } from '@/utils/formatStoredDate';

/**
 * PartnerSummary
 * @description Minimal shape used by this modal for display.
 */
/**
 * PartnerSummary
 *
 * @description Minimal shape used by this modal for display.
 */
export interface PartnerSummary {
  partnerId: string;
  displayName: string | null;
  lastMessage?: string;
  lastAt?: string | null;
  unreadCount: number;
  /**
   * conversationId
   * @description Optional conversation identifier when available from backend payloads.
   * This helps perform precise mark-read operations.
   */
  conversationId?: string | null;
  /**
   * photoUrl
   * @description Optional partner photo URL when available in payload.
   */
  photoUrl?: string | null;
}

/**
 * PartnerDetails
 * @description Optional resolved partner details.
 */
export interface PartnerDetails {
  displayName?: string | null;
  photoUrl?: string | null;
}

/**
 * Props for MessagesModal
 */
interface MessagesModalProps {
  open: boolean;
  onClose: () => void;
  onViewProfile?: (partnerId: string) => void;
  /**
   * onChat
   * @param partnerId partner id
   * @param displayName resolved display name (optional)
   */
  onChat?: (partnerId: string, displayName?: string | null) => void;
  navigateToSearch?: () => void;
}

/**
 * Avatar small inline used to show partner photo (very small, local)
 */
function SmallAvatar({ name, src }: { name?: string | null; src?: string | null }) {
  const initials = (name ?? '')
    .split(/\s+/)
    .map((p) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase() || '?';
  return (
    <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 flex-shrink-0 overflow-hidden">
      {src ? <img src={src} alt={name ?? 'avatar'} className="w-full h-full object-cover" /> : <span className="font-semibold">{initials}</span>}
    </div>
  );
}

/**
 * MessagesModal
 *
 * @description Render a list of partner conversation summaries.
 * - Ensures partner userId is shown under the name.
 * - Calls onChat(partnerId, displayName) so consumer can open ChatModal with name.
 */
export default function MessagesModal({ open, onClose, onViewProfile, onChat, navigateToSearch }: MessagesModalProps) {
  const user = useAuthStore((s) => s.user);
  const myUserId = user?.userId ?? null;
  const navigate = useNavigate();

  // Live SSE messages (may be empty) and markRead helper
  const { messages, markRead } = useChat(myUserId ?? undefined, Boolean(myUserId));

  // Local caches for resolved names & details (component state)
  const [names, setNames] = useState<Record<string, string | null>>({});
  const [details, setDetails] = useState<Record<string, PartnerDetails>>({});

  // Fallback summaries (loaded from various endpoints) and loading state
  const [fallbackSummaries, setFallbackSummaries] = useState<PartnerSummary[] | null>(null);
  const [loadingFallback, setLoadingFallback] = useState<boolean>(false);
  const fetchingFallbackRef = useRef<boolean>(false);
  const [rawResponse, setRawResponse] = useState<any>(null);

  /**
   * readPartners
   *
   * @description Local override map partnerId => true to treat partner's messages as read immediately
   * after attempting mark-read on server. This updates UI immediately; server-side should perform
   * final DB update.
   */
  const [readPartners, setReadPartners] = useState<Record<string, boolean>>({});

  // Track modal view when opened
  useEffect(() => {
    if (open) {
      trackModalView('Messages Modal');
    }
  }, [open]);

  /**
   * build summaries from SSE messages
   */
  const summariesFromSSE: PartnerSummary[] = useMemo(() => {
    if (!myUserId) return [];
    const map: Record<string, PartnerSummary> = {};
    const items: ChatMessage[] = Array.isArray(messages) ? messages : [];

    for (const m of items) {
      if (m.fromUserId !== myUserId && m.toUserId !== myUserId) continue;
      const partnerId = m.fromUserId === myUserId ? m.toUserId : m.fromUserId;
      if (!partnerId) continue;
      const existing = map[partnerId] ?? { partnerId, displayName: null, lastMessage: undefined, lastAt: null, unreadCount: 0 };

      const mTime = m.createdAt ? new Date(m.createdAt).getTime() : 0;
      const existingTime = existing.lastAt ? new Date(existing.lastAt).getTime() : 0;
      if (!existing.lastAt || mTime >= existingTime) {
        existing.lastAt = m.createdAt ?? null;
        existing.lastMessage = m.message;
      }

      if (m.toUserId === myUserId && !m.isRead) {
        existing.unreadCount = (existing.unreadCount || 0) + 1;
      }

      map[partnerId] = existing;
    }

    return Object.values(map).sort((a, b) => {
      const ta = a.lastAt ? new Date(a.lastAt).getTime() : 0;
      const tb = b.lastAt ? new Date(b.lastAt).getTime() : 0;
      return tb - ta;
    });
  }, [messages, myUserId]);

  /**
   * displayedSummaries prefers live SSE summaries, otherwise fallback list.
   */
  const displayedSummaries = useMemo(() => {
    if (summariesFromSSE && summariesFromSSE.length > 0) return summariesFromSSE;
    return fallbackSummaries ?? [];
  }, [summariesFromSSE, fallbackSummaries]);

  /**
   * totalTrying: count of partners with unread messages (respecting local readPartners override)
   */
  const totalTrying = useMemo(() => {
    return displayedSummaries.reduce((acc, s) => {
      const unread = readPartners[s.partnerId] ? 0 : (s.unreadCount || 0);
      return acc + (unread > 0 ? 1 : 0);
    }, 0);
  }, [displayedSummaries, readPartners]);

  /**
   * resolvePartnerDetails
   *
   * @description Lazy resolve display name & photo for a partnerId and set component caches.
   * Returns the resolved displayName (string | null) so callers can use it immediately.
   *
   * @param partnerId partner id
   * @returns resolved display name or null
   */
  async function resolvePartnerDetails(partnerId: string): Promise<string | null> {
    if (!partnerId) return null;

    // If we already have either name or details cached, return the cached name (explicit)
    if (names[partnerId] !== undefined) {
      return names[partnerId] ?? null;
    }
    if (details[partnerId] !== undefined) {
      return details[partnerId]?.displayName ?? null;
    }

    // Try module-level helper utils when available
    try {
      const { fetchUserProfile } = await import('@/utils/userApi');
      const profile = await fetchUserProfile(partnerId);
      if (profile) {
        const display =
          profile.displayName ??
          ([profile.firstName, profile.lastName].filter(Boolean).join(' ').trim() || null);
        const photoUrl =
          (profile.photoUrl as string | undefined) ??
          (profile.profilePhoto as string | undefined) ??
          null;

        setDetails((s) => ({ ...s, [partnerId]: { displayName: display, photoUrl } }));
        setNames((s) => ({ ...s, [partnerId]: display ?? null }));
        return display ?? null;
      }
    } catch {
      // ignore failures, fall through to set null
    }

    // Fallback to null to avoid repeated requests
    setDetails((s) => ({ ...s, [partnerId]: { displayName: null, photoUrl: null } }));
    setNames((s) => ({ ...s, [partnerId]: null }));
    return null;
  }

  // Prefetch visible partners' details
  useEffect(() => {
    const ids = displayedSummaries.slice(0, 8).map((s) => s.partnerId);
    for (const id of ids) resolvePartnerDetails(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayedSummaries]);

  /**
   * deriveSummariesFromPayload
   * @description Best-effort parser for various endpoint payloads (unchanged from previous logic).
   */
  /**
   * deriveSummariesFromPayload
   *
   * @description Best-effort parser for various endpoint payloads (unchanged from previous logic),
   * but enhanced to extract conversation identifiers when available so mark-read requests can be
   * more precise (conversationId, convId, conversation, threadId, etc).
   *
   * @param payload any
   * @returns PartnerSummary[]
   */
  function deriveSummariesFromPayload(payload: any): PartnerSummary[] {
    /**
     * extractConversationId
     *
     * @description Inspect an object for common conversation id keys and return the first match.
     * This handles many backend naming conventions.
     */
    function extractConversationId(obj: any): string | null {
      if (!obj || typeof obj !== 'object') return null;
      const keys = [
        'conversationId',
        'convId',
        'conversation',
        'conversation_id',
        'conv_id',
        'threadId',
        'thread_id',
        'thread',
        'id'
      ];
      for (const k of keys) {
        if (obj[k] !== undefined && obj[k] !== null && String(obj[k]).trim() !== '') {
          return String(obj[k]);
        }
      }
      return null;
    }

    try {
      if (!payload) return [];
      if (payload.data && (Array.isArray(payload.data) || typeof payload.data === 'object')) {
        const maybe = payload.data;
        if (Array.isArray(maybe)) return deriveSummariesFromPayload(maybe);
        payload = payload.data;
      }

      if (Array.isArray(payload)) {
        const out = payload
          .map((it) => {
            if (!it) return null;

            // Message-shaped items (common)
            if (it.fromUserId && it.toUserId) {
              const partnerId = String(it.fromUserId === myUserId ? it.toUserId : it.fromUserId);
              const convId = extractConversationId(it);
              return {
                partnerId,
                displayName: null,
                lastMessage: it.message ?? it.text ?? undefined,
                lastAt: it.createdAt ?? it.updatedAt ?? null,
                unreadCount: it.toUserId === myUserId && !it.isRead ? 1 : 0,
                conversationId: convId ?? null,
                photoUrl: (it.photoUrl ?? it.profilePhoto ?? null) as string | null
              } as PartnerSummary;
            }

            // Generic item shapes that may represent a summary row
            const partnerId = it.senderId ?? it.partnerId ?? it.userId ?? it.otherUserId ?? it.id ?? it.user_id;
            if (!partnerId) return null;

            const lastMessage = it.lastMessage ?? it.message ?? it.preview ?? it.summary ?? undefined;
            const lastAt = it.lastAt ?? it.updatedAt ?? it.createdAt ?? it.time ?? null;
            const unreadCount = Number(it.unreadCount ?? it.unread ?? it.unread_count ?? 0) || 0;
            const convId = extractConversationId(it);

            return {
              partnerId: String(partnerId),
              displayName: null,
              lastMessage,
              lastAt,
              unreadCount,
              conversationId: convId ?? null,
              photoUrl: (it.photoUrl ?? it.profilePhoto ?? null) as string | null
            } as PartnerSummary;
          })
          .filter(Boolean);
        return out;
      }

      const candidateArrays = ['senders', 'conversations', 'items', 'list', 'rows', 'messages', 'threads'];
      for (const k of candidateArrays) {
        if (Array.isArray(payload[k])) {
          return deriveSummariesFromPayload(payload[k]);
        }
      }

      const keys = Object.keys(payload || {});
      if (keys.length > 0) {
        const possible = keys
          .map((k) => {
            const v = payload[k];
            if (!v) return null;
            if (typeof v === 'object') {
              const lastMessage = v.lastMessage ?? v.message ?? undefined;
              const lastAt = v.lastAt ?? v.updatedAt ?? v.createdAt ?? v.time ?? null;
              const unreadCount = Number(v.unreadCount ?? v.unread ?? 0) || 0;
              const convId = extractConversationId(v) ?? (k && String(k).match(/^[0-9a-f-]{4,}$/i) ? String(k) : null);
              const partnerId = v.userId ?? v.partnerId ?? v.senderId ?? k;
              if (!partnerId) return null;
              return {
                partnerId: String(partnerId),
                displayName: null,
                lastMessage,
                lastAt,
                unreadCount,
                conversationId: convId ?? null,
                photoUrl: (v.photoUrl ?? v.profilePhoto ?? null) as string | null
              } as PartnerSummary;
            }
            return null;
          })
          .filter(Boolean);
        if (possible.length > 0) return possible;
      }

      return [];
    } catch (err) {
      return [];
    }
  }

  /**
   * tryFetchEndpoints
   * @description Try a number of common endpoints to fetch fallback conversation lists.
   */
  async function tryFetchEndpoints(userId: string): Promise<any> {
    const endpoints = [
      `/api/chat/unread-senders/${encodeURIComponent(userId)}`,
      `/api/chat/unread-senders?userId=${encodeURIComponent(userId)}`,
      `/api/chat/conversations/${encodeURIComponent(userId)}`,
      `/api/chat/conversations?userId=${encodeURIComponent(userId)}`,
      `/chat/unread-senders/${encodeURIComponent(userId)}`,
      `/chat/conversations/${encodeURIComponent(userId)}`,
      `/api/messages/unread/${encodeURIComponent(userId)}`,
      `/messages/unread/${encodeURIComponent(userId)}`,
      `/api/chat/list/${encodeURIComponent(userId)}`,
      `/api/chat/list?userId=${encodeURIComponent(userId)}`,
      `/api/user-messages/${encodeURIComponent(userId)}`,
      `/api/user-messages?userId=${encodeURIComponent(userId)}`,
      `/api/messages?userId=${encodeURIComponent(userId)}`,
      `/api/chat?userId=${encodeURIComponent(userId)}`,
      `/api/chat`,
      `/api/messages`,
      `/messages`,
      `/chat`
    ];

    for (const p of endpoints) {
      try {
        const res = await fetchJSON(p);
        if (res !== undefined && res !== null) {
          return { url: p, payload: res };
        }
      } catch (err) {
        try {
          const fallback = await fetch(p, { method: 'GET' });
          if (fallback.ok) {
            const txt = await fallback.text();
            try {
              const j = JSON.parse(txt);
              if (j !== undefined && j !== null) return { url: p, payload: j };
            } catch {
              // not JSON
            }
          }
        } catch {
          // ignore
        }
      }
    }
    return null;
  }

  /**
   * fetchFallbackSummaries
   */
  async function fetchFallbackSummaries(userId: string) {
    if (!userId) return;
    if (fetchingFallbackRef.current) return;
    fetchingFallbackRef.current = true;
    setLoadingFallback(true);
    setRawResponse(null);
    setFallbackSummaries(null);

    try {
      const fetched = await tryFetchEndpoints(userId);
      if (!fetched) {
        setRawResponse(null);
        setFallbackSummaries([]);
        return;
      }

      setRawResponse({ url: fetched.url, payload: fetched.payload });

      let derived = deriveSummariesFromPayload(fetched.payload);

      // if derived is empty, attempt message array probe
      if ((!derived || derived.length === 0) && fetched.payload) {
        /**
         * findMessageArrays
         *
         * @description Recursively search payload for arrays that look like message lists.
         */
        function findMessageArrays(obj: any): any[] {
          if (!obj || typeof obj !== 'object') return [];
          if (Array.isArray(obj)) {
            if (obj.length > 0 && (obj[0].fromUserId || obj[0].toUserId || obj[0].message)) return obj;
            for (const it of obj) {
              const found = findMessageArrays(it);
              if (found && found.length > 0) return found;
            }
            return [];
          }
          for (const k of Object.keys(obj)) {
            const found = findMessageArrays(obj[k]);
            if (found && found.length > 0) return found;
          }
          return [];
        }

        /**
         * extractConversationFromMessage
         *
         * @description Best-effort extract conversation identifier from a message-shaped object.
         */
        function extractConversationFromMessage(m: any): string | null {
          if (!m || typeof m !== 'object') return null;
          return (
            m.conversationId ??
            m.convId ??
            m.threadId ??
            m.thread ??
            m.conversation ??
            m.conversation_id ??
            m.conv_id ??
            null
          );
        }

        const messagesArray = findMessageArrays(fetched.payload);
        if (messagesArray && messagesArray.length > 0) {
          const map: Record<string, PartnerSummary> = {};
          for (const m of messagesArray) {
            if (!m) continue;
            const from = m.fromUserId ?? m.from ?? m.senderId;
            const to = m.toUserId ?? m.to ?? m.recipientId;
            if (!from || !to) continue;
            if (from !== userId && to !== userId) continue;
            const partnerId = from === userId ? to : from;
            const existing = map[partnerId] ?? { partnerId: String(partnerId), displayName: null, lastMessage: undefined, lastAt: null, unreadCount: 0, conversationId: null, photoUrl: null };
            const mTime = m.createdAt ?? m.updatedAt ?? m.time ?? null;
            if (!existing.lastAt || (mTime && new Date(mTime).getTime() >= new Date(existing.lastAt ?? 0).getTime())) {
              existing.lastAt = mTime;
              existing.lastMessage = m.message ?? m.text ?? undefined;
            }
            if ((m.toUserId === userId || m.to === userId) && !m.isRead) {
              existing.unreadCount = (existing.unreadCount || 0) + 1;
            }

            // Prefer conversation id when present on messages
            const convId = extractConversationFromMessage(m);
            if (convId) existing.conversationId = String(convId);

            // Photo/url hints
            existing.photoUrl = existing.photoUrl ?? (m.photoUrl ?? m.profilePhoto ?? null);

            map[partnerId] = existing;
          }
          derived = Object.values(map).sort((a, b) => {
            const ta = a.lastAt ? new Date(a.lastAt as string).getTime() : 0;
            const tb = b.lastAt ? new Date(b.lastAt as string).getTime() : 0;
            return tb - ta;
          });
        }
      }

      setFallbackSummaries(derived || []);
    } catch (err) {
      setFallbackSummaries([]);
    } finally {
      fetchingFallbackRef.current = false;
      setLoadingFallback(false);
    }
  }

  // When modal opens: fetch fallback summaries if live SSE empty
  useEffect(() => {
    if (!open) return;
    if (!myUserId) return;
    if (summariesFromSSE && summariesFromSSE.length > 0) {
      setFallbackSummaries(null);
      setRawResponse(null);
      return;
    }
    if (fallbackSummaries !== null) return;
    fetchFallbackSummaries(myUserId).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, myUserId, summariesFromSSE.length]);

  if (!open) return null;

  /**
   * buildDisplayNameFallback
   *
   * @description Return a string display name fallback when resolved displayName is not available.
   * Falls back to cached name, initials or partnerId to ensure ChatModal header shows immediately.
   */
  function buildDisplayNameFallback(partnerId: string): string {
    const cachedName = names[partnerId] ?? details[partnerId]?.displayName;
    if (cachedName) return cachedName;
    // create initials-like fallback from partnerId (split by non-alpha)
    const parts = String(partnerId).split(/[^A-Za-z0-9]+/).filter(Boolean);
    if (parts.length >= 2) {
      return `${parts[0]} ${parts[1]}`; // somewhat readable
    }
    if (parts.length === 1) {
      const p = parts[0];
      return p.length <= 10 ? p : p.slice(0, 10) + '...';
    }
    return partnerId;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" onClick={onClose} aria-hidden />

      {/* Modal container */}
      <div className="relative w-full max-w-3xl mx-auto">
        <div role="dialog" aria-modal="true" tabIndex={-1} className="overflow-hidden">
          <div className="bg-white rounded-lg shadow-lg ring-1 ring-slate-100 overflow-hidden">
            {/* Header */}
            <div className="px-4 py-3 flex items-center justify-between bg-gradient-to-r from-indigo-600 to-pink-600 text-white">
              <div className="flex items-center space-x-3">
                <Users className="w-5 h-5 text-white/90" />
                <div>
                  <div className="text-sm font-semibold">Conversations</div>
                  <div className="text-xs text-white/90">Recent and past chats</div>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <div className="inline-flex items-center px-3 py-1 rounded-full bg-white text-indigo-600 text-sm font-semibold">
                  {displayedSummaries.length} total
                </div>
                <div className="inline-flex items-center px-2 py-1 rounded-full bg-white/20 text-white text-sm font-medium">
                  {totalTrying} trying
                </div>
                <Button size="sm" className="bg-white/10 text-white" onClick={() => { setFallbackSummaries(null); if (myUserId) fetchFallbackSummaries(myUserId); }}>
                  Refresh
                </Button>
                <Button size="sm" className="bg-transparent text-white" onClick={onClose}>
                  Close
                </Button>
              </div>
            </div>

            {/* Content */}
            <div className="p-4 max-h-[60vh] overflow-y-auto space-y-3">
              {loadingFallback && displayedSummaries.length === 0 ? (
                <div className="text-center py-8">
                  <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                  <div className="text-sm text-slate-600">Loading conversations...</div>
                </div>
              ) : displayedSummaries.length === 0 ? (
                <div className="text-center text-sm text-slate-600 py-4">
                  No conversations yet
                  <div className="mt-3 flex justify-center space-x-2">
                    <Button size="sm" className="bg-indigo-600 text-white" onClick={() => { onClose(); navigateToSearch?.(); }}>
                      Find Matches
                    </Button>
                    <Button size="sm" className="bg-transparent text-slate-700" onClick={() => { setFallbackSummaries(null); if (myUserId) fetchFallbackSummaries(myUserId); }}>
                      Retry
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="grid gap-3">
                  {displayedSummaries.map((c) => (
                    <div key={c.partnerId} className="bg-white rounded-lg border border-slate-100 p-3 shadow-sm flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <SmallAvatar src={details[c.partnerId]?.photoUrl ?? null} name={details[c.partnerId]?.displayName ?? names[c.partnerId] ?? c.partnerId} />
                        <div className="min-w-0">
                          <div className="text-sm font-medium truncate">
                            {details[c.partnerId]?.displayName ?? names[c.partnerId] ?? c.partnerId}
                          </div>

                          {/* Partner userId shown below name as requested */}
                          <div className="text-xs text-slate-500 truncate">{c.partnerId}</div>

                          <div className="text-xs text-slate-500 truncate" style={{ maxWidth: 320 }}>
                            {c.lastMessage ?? 'No messages yet'}
                          </div>
                          <div className="text-xs text-slate-400 mt-1">{c.lastAt ? formatStoredDate(c.lastAt) : ''}</div>
                        </div>
                      </div>

                      <div className="flex flex-col items-end space-y-2">
                        <div className="flex items-center space-x-2">
                          {/* View button navigates to /search-profile/:userId */}
                          <Button
                            size="sm"
                            className="bg-amber-600 text-white"
                            onClick={async () => {
                              // Resolve partner details (name/photo) to ensure any caches are warmed
                              await resolvePartnerDetails(c.partnerId);
                              try { onClose(); } catch {}
                              // Always navigate to UserSearchProfile route (search-profile)
                              // Do NOT call onViewProfile here to avoid parents navigating to ProfileView.
                              navigate(`/search-profile/${encodeURIComponent(c.partnerId)}`);
                            }}
                          >
                            View
                          </Button>

                          {/* Chat button:
                              - Resolve partner details for immediate header display
                              - Try to mark messages read (useChat.markRead + fallback POST)
                              - Update local read override so UI clears unread badge immediately
                              - Close modal and call onChat(partnerId, displayName)
                          */}
                          <Button
                            size="sm"
                            className="bg-indigo-600 text-white"
                            onClick={async () => {
                              /**
                               * Chat button flow improvements:
                               * - Resolve partner details and obtain a resolved displayName
                               *   synchronously from this function so we can dispatch it reliably.
                               * - Open the chat (dispatch event / call callback) BEFORE marking messages read.
                               *   This avoids marking messages read in DB before the ChatModal has a chance to fetch
                               *   and display them.
                               * - Perform mark-read actions after a short delay to let the ChatModal initialize.
                               */

                              // Resolve partner display name (returns string | null)
                              const resolvedName = await resolvePartnerDetails(c.partnerId);
                              const display = resolvedName ?? buildDisplayNameFallback(c.partnerId);

                              // Close the MessagesModal first so ChatModal can open cleanly
                              try { onClose(); } catch {}

                              // Dispatch an app-level event so top-level App can open ChatModal immediately with the display value
                              try {
                                // Persist intent so consumers that mount slightly later can still pick up the displayName.
                                // Use sessionStorage (tab-scoped) with a short-lived timestamp.
                                try {
                                  sessionStorage.setItem(
                                    'sider.openChat',
                                    JSON.stringify({ partnerId: c.partnerId, displayName: display, ts: Date.now() })
                                  );
                                } catch (e) {
                                  // ignore sessionStorage failures
                                }

                                window.dispatchEvent(new CustomEvent('sider-open-chat', { detail: { partnerId: c.partnerId, displayName: display } }));
                              } catch (err) {
                                // ignore
                              }

                              // Call parent callback for compatibility (open chat)
                              try {
                                onChat?.(c.partnerId, display);
                              } catch {}

                              /**
                               * Defer marking messages as read to allow ChatModal to fetch and render messages.
                               * This prevents the backend from marking messages read before the UI displays them.
                               */
                              setTimeout(async () => {
                                try {
                                  // Attempt markRead via useChat hook (best-effort)
                                  await markRead?.(c.partnerId);
                                } catch {
                                  // ignore
                                }

                                // Fallback POST to common path (prefer conversationId when available)
                                try {
                                  const convId = (c as any).conversationId ?? (c as any).convId ?? (c as any).conversation ?? null;
                                  const payload: Record<string, any> = { userId: myUserId };
                                  if (convId) {
                                    payload.conversationId = convId;
                                  } else {
                                    payload.withUserId = c.partnerId;
                                  }

                                  await fetch('/api/chat/mark-read', {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify(payload)
                                  });
                                } catch (err) {
                                  // ignore network/POST failures (best-effort)
                                }

                                // Mark partner as read locally so UI updates immediately
                                setReadPartners((prev) => ({ ...prev, [c.partnerId]: true }));

                                // Optimistically update fallbackSummaries to clear unread badge
                                setFallbackSummaries((prev) => {
                                  if (!prev) return prev;
                                  return prev.map((it) => (it.partnerId === c.partnerId ? { ...it, unreadCount: 0 } : it));
                                });

                                // Refresh fallback summaries from server (best-effort) to sync unread counts with backend
                                try {
                                  if (myUserId) fetchFallbackSummaries(myUserId).catch(() => {});
                                } catch {}
                              }, 500);
                            }}
                          >
                            <MessageCircle className="w-4 h-4 mr-2" />
                            Chat
                          </Button>
                        </div>

                        {/* Unread badge (respects local read override) */}
                        {(() => {
                          const displayedUnread = readPartners[c.partnerId] ? 0 : (c.unreadCount || 0);
                          return displayedUnread > 0 ? (
                            <div className="inline-flex items-center px-2 py-0.5 rounded-full bg-red-50 text-red-700 text-xs">
                              {displayedUnread}
                            </div>
                          ) : (
                            <div className="text-xs text-slate-400">No unread</div>
                          );
                        })()}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer - no 'Conversations loaded from fallback' text as requested */}
            <div className="px-4 py-3 border-t flex items-center justify-end gap-2">
              <Button size="sm" className="bg-transparent text-slate-700" onClick={() => { setFallbackSummaries(null); if (myUserId) fetchFallbackSummaries(myUserId); }}>
                Retry
              </Button>
              <Button size="sm" className="bg-white text-indigo-600" onClick={onClose}>Close</Button>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}